/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Default enforcement action.
 */
export const RiskPresetAction = {
  Flag: "flag",
  Warn: "warn",
  Block: "block",
  Quarantine: "quarantine",
} as const;
/**
 * Default enforcement action.
 */
export type RiskPresetAction = ClosedEnum<typeof RiskPresetAction>;

/**
 * Policy type the preset creates.
 */
export const RiskPresetPolicyType = {
  Standard: "standard",
  PromptBased: "prompt_based",
} as const;
/**
 * Policy type the preset creates.
 */
export type RiskPresetPolicyType = ClosedEnum<typeof RiskPresetPolicyType>;

/**
 * One use-case preset a risk policy can start from.
 */
export type RiskPreset = {
  /**
   * Default enforcement action.
   */
  action: RiskPresetAction;
  /**
   * What the preset protects against, in plain language.
   */
  description: string;
  /**
   * Stable preset identifier (e.g. 'secrets_and_credentials').
   */
  id: string;
  /**
   * Human-readable preset name.
   */
  label: string;
  /**
   * Policy type the preset creates.
   */
  policyType: RiskPresetPolicyType;
  /**
   * Presidio entities enabled when sources include presidio.
   */
  presidioEntities: Array<string>;
  /**
   * Judge instruction for a prompt-based preset; empty otherwise.
   */
  prompt: string;
  /**
   * True when the preset is inert until approved_email_domains is supplied.
   */
  requiresApprovedEmailDomains: boolean;
  /**
   * Default CVSS-style severity (0.1-10).
   */
  score: number;
  /**
   * Detector sources enabled by a standard preset; empty for prompt-based presets.
   */
  sources: Array<string>;
  /**
   * Default message shown to the user when the policy warns or blocks; empty when none.
   */
  userMessage: string;
};

/** @internal */
export const RiskPresetAction$inboundSchema: z.ZodMiniEnum<
  typeof RiskPresetAction
> = z.enum(RiskPresetAction);

/** @internal */
export const RiskPresetPolicyType$inboundSchema: z.ZodMiniEnum<
  typeof RiskPresetPolicyType
> = z.enum(RiskPresetPolicyType);

/** @internal */
export const RiskPreset$inboundSchema: z.ZodMiniType<RiskPreset, unknown> = z
  .pipe(
    z.object({
      action: RiskPresetAction$inboundSchema,
      description: z.string(),
      id: z.string(),
      label: z.string(),
      policy_type: RiskPresetPolicyType$inboundSchema,
      presidio_entities: z.array(z.string()),
      prompt: z.string(),
      requires_approved_email_domains: z.boolean(),
      score: z.number(),
      sources: z.array(z.string()),
      user_message: z.string(),
    }),
    z.transform((v) => {
      return remap$(v, {
        "policy_type": "policyType",
        "presidio_entities": "presidioEntities",
        "requires_approved_email_domains": "requiresApprovedEmailDomains",
        "user_message": "userMessage",
      });
    }),
  );

export function riskPresetFromJSON(
  jsonString: string,
): SafeParseResult<RiskPreset, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RiskPreset$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RiskPreset' from JSON`,
  );
}
